import { type UseQueryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { hasEgovApplicationBatchProcessingRows } from '@/features/labor-core/-components/egov-application-confirmation/lib/egovProcessingStatus';
import { getEgovApplicationStepsByPackageInstances, getEgovApplicationStepsByTag, getYearEndAdjustmentDocumentTabCounts, getYearEndAdjustmentInstanceDocuments, getYearEndAdjustmentInstancesByTag, getOperationPackageInstances, getOperationPackageInstanceTags, getOperationPackagePatterns, getOperationPackages, getOperationTaskCountInProgress, getPackageInstanceTag, postOperationPackagePatterns } from '@/features/operation-packages/api';
import type { EgovApplicationStepsByPackageInstancesQuery, EgovApplicationStepsByTagQuery, YearEndAdjustmentDocumentTabCountsQuery, YearEndAdjustmentInstanceDocumentsResponse, YearEndAdjustmentInstancesByTagQuery, OperationPackagesResponse, OwnerProduct, PackageType } from '@/features/operation-packages/api';
import { QUERY_PRESETS } from '@/lib/query';
import { usePollingQuery } from '@/lib/query/usePollingQuery';
import { queryClient } from '@/lib/queryClient';

// --- Query Keys ---

export const operationPackageQueryKeys = {
  all: (ownerProduct: OwnerProduct) => ['operation-packages', ownerProduct] as const,
  lists: (ownerProduct: OwnerProduct) => [...operationPackageQueryKeys.all(ownerProduct), 'list'] as const,
  list: (ownerProduct: OwnerProduct) => [...operationPackageQueryKeys.lists(ownerProduct)] as const,
  countInProgress: (ownerProduct: OwnerProduct, id: string) => [...operationPackageQueryKeys.all(ownerProduct), id, 'count-in-progress'] as const,
  patterns: (ownerProduct: OwnerProduct, id: string) => [...operationPackageQueryKeys.all(ownerProduct), id, 'patterns'] as const,
  patternCountInProgress: (ownerProduct: OwnerProduct, id: string, patternId: string) => [...operationPackageQueryKeys.patterns(ownerProduct, id), patternId, 'count-in-progress'] as const,
  instances: (ownerProduct: OwnerProduct, patternId: string, page?: number, pageSize?: number, targetId?: string) => [...operationPackageQueryKeys.all(ownerProduct), 'instances', patternId, page, pageSize, targetId] as const,
  governmentApplicationInstances: (ownerProduct: OwnerProduct, patternId: string, page?: number, pageSize?: number, targetId?: string) => [...operationPackageQueryKeys.all(ownerProduct), 'government-application-instances', patternId, page, pageSize, targetId] as const,
  instanceTags: (ownerProduct: OwnerProduct, patternId: string, page?: number, pageSize?: number) => [...operationPackageQueryKeys.all(ownerProduct), 'instance-tags', patternId, page, pageSize] as const,
  /** 行政申請確認一覧（タグ別）の invalidate 用プレフィックス */
  egovApplicationStepsByTagRoot: (ownerProduct: OwnerProduct, tagId: string, operationPackagePatternId: string) => [...operationPackageQueryKeys.all(ownerProduct), 'egov-confirmation-steps', tagId, operationPackagePatternId] as const,
  egovApplicationStepsByTag: (ownerProduct: OwnerProduct, tagId: string, operationPackagePatternId: string, filters: {
    page?: number;
    pageSize?: number;
    orderBy?: string;
    orderDirection?: string;
    searchText?: string;
    status?: readonly string[];
  }) => [...operationPackageQueryKeys.egovApplicationStepsByTagRoot(ownerProduct, tagId, operationPackagePatternId), filters.page, filters.pageSize, filters.orderBy, filters.orderDirection, filters.searchText, filters.status] as const,
  /** 行政申請確認一覧（アーカイブ・Package Instance 指定）の invalidate 用プレフィックス */
  egovApplicationStepsByPackageInstancesRoot: (ownerProduct: OwnerProduct, operationPackageInstanceIds: readonly string[]) => [...operationPackageQueryKeys.all(ownerProduct), 'egov-confirmation-steps-by-package-instances', operationPackageInstanceIds] as const,
  egovApplicationStepsByPackageInstances: (ownerProduct: OwnerProduct, operationPackageInstanceIds: readonly string[], filters: {
    page?: number;
    pageSize?: number;
    orderBy?: string;
    orderDirection?: string;
    searchText?: string;
    status?: readonly string[];
  }) => [...operationPackageQueryKeys.egovApplicationStepsByPackageInstancesRoot(ownerProduct, operationPackageInstanceIds), filters.page, filters.pageSize, filters.orderBy, filters.orderDirection, filters.searchText, filters.status] as const,
  yearEndAdjustmentInstancesByTagRoot: (ownerProduct: OwnerProduct, tagId: string) => [...operationPackageQueryKeys.all(ownerProduct), 'year-end-adjustment-instances', tagId] as const,
  yearEndAdjustmentInstancesByTag: (ownerProduct: OwnerProduct, tagId: string, filters: {
    page?: number;
    pageSize?: number;
    orderBy?: string;
    orderDirection?: string;
    searchText?: string;
    status?: readonly string[];
    documentFocus?: string;
  }) => [...operationPackageQueryKeys.yearEndAdjustmentInstancesByTagRoot(ownerProduct, tagId), filters.page, filters.pageSize, filters.orderBy, filters.orderDirection, filters.searchText, filters.status, filters.documentFocus] as const,
  yearEndAdjustmentDocumentTabCounts: (ownerProduct: OwnerProduct, tagId: string, filters: {
    searchText?: string;
    status?: readonly string[];
  }) => [...operationPackageQueryKeys.yearEndAdjustmentInstancesByTagRoot(ownerProduct, tagId), 'document-tab-counts', filters.searchText, filters.status] as const,
  packageInstanceTag: (ownerProduct: OwnerProduct, tagId: string) => [...operationPackageQueryKeys.all(ownerProduct), 'instance-tag', tagId] as const,
  /** 年末調整 インスタンス書類一覧（ドロワー用）の invalidate root */
  yearEndAdjustmentInstanceDocumentsRoot: (ownerProduct: OwnerProduct, instanceId: string) => [...operationPackageQueryKeys.all(ownerProduct), 'year-end-adjustment-instance-documents', instanceId] as const,
  yearEndAdjustmentInstanceDocuments: (ownerProduct: OwnerProduct, instanceId: string) => [...operationPackageQueryKeys.yearEndAdjustmentInstanceDocumentsRoot(ownerProduct, instanceId)] as const
} as const;

// --- Hooks ---

/**
 * `useGetOperationPackages` の呼び出し側が上書きできる範囲。
 *
 * キャッシュ方針（`QUERY_PRESETS`）とエラー方針（`meta.disableGlobalErrorHandling`）だけを
 * 開けている。`queryKey` / `queryFn` は共有キャッシュの同一性を壊すので開けない。
 */
type OperationPackagesQueryOptions = Pick<UseQueryOptions<OperationPackagesResponse>, 'staleTime' | 'gcTime' | 'refetchOnWindowFocus' | 'refetchOnReconnect' | 'refetchOnMount' | 'meta'>;

/**
 * Operation Packageの一覧を取得する
 *
 * `options` 省略時は React Query の既定（`staleTime: 0` + focus ごとに refetch、
 * グローバルエラーダイアログ有効）。既存の呼び出し側の挙動を変えないための既定であり、
 * 画面の方針に合わせたい場合は呼び出し側で `QUERY_PRESETS` / `meta` を渡す。
 */
export const useGetOperationPackages = (ownerProduct: OwnerProduct, options?: OperationPackagesQueryOptions) => {
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: operationPackageQueryKeys.list(ownerProduct),
    queryFn: () => getOperationPackages(ownerProduct),
    ...options
  });
  return {
    data,
    isLoading,
    error
  };
};

/**
 * 指定されたパッケージタイプのパッケージIDを取得するReact Queryフック
 * @param ownerProduct プロダクト
 * @param packageType 取得したいパッケージのタイプ
 */
export function useOperationPackageIdByType(ownerProduct: OwnerProduct, packageType: PackageType) {
  // 全パッケージを取得
  const {
    data: allPackages,
    error,
    isLoading
  } = useGetOperationPackages(ownerProduct);

  // パッケージタイプに対応するIDをメモ化して算出
  const packageId = useMemo(() => {
    if (!allPackages || !packageType) return null;
    const targetPackage = allPackages.find(pkg => pkg.packageType === packageType);
    if (targetPackage) {
      return targetPackage.operationPackageId;
    }
    return null;
  }, [allPackages, packageType]);
  return {
    data: packageId,
    error,
    isLoading,
    isSuccess: !isLoading && !error,
    isError: !!error,
    isLoadingError: !!error
  };
}

/**
 * Operation Packageのパターンの定義一覧を取得する
 */
export const useGetOperationPackagePatterns = (ownerProduct: OwnerProduct, id: string, options?: {
  enabled?: boolean;
}) => {
  const {
    data,
    isLoading,
    error
  } = useSuspenseQuery({
    queryKey: operationPackageQueryKeys.patterns(ownerProduct, id),
    queryFn: () => {
      if (options?.enabled === false || !id) {
        return Promise.resolve([]);
      }
      return getOperationPackagePatterns(ownerProduct, id);
    }
  });
  return {
    data,
    isLoading,
    error
  };
};

/**
 * 特定のOperation Packageのパターンを作成する
 */
export const usePostOperationPackagePatterns = (ownerProduct: OwnerProduct, id: string, options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const {
    mutate,
    isPending,
    error
  } = useMutation({
    mutationFn: () => postOperationPackagePatterns(ownerProduct, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operationPackageQueryKeys.patterns(ownerProduct, id)
      });
      options?.onSuccess?.();
    },
    onError: error => {
      options?.onError?.(error);
    }
  });
  return {
    mutate,
    isPending,
    error
  };
};

/**
 * 特定のOperation Packageのパターンのタスクの実行中の数を取得する
 */
export const useGetOperationTaskCountInProgress = ({
  ownerProduct,
  operationPackageId: id,
  patternId
}: {
  ownerProduct: OwnerProduct;
  operationPackageId: string;
  patternId: string;
}) => {
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: operationPackageQueryKeys.patternCountInProgress(ownerProduct, id, patternId),
    queryFn: () => getOperationTaskCountInProgress(ownerProduct, id, patternId)
  });
  return {
    data,
    isLoading,
    error
  };
};

/**
 * 特定のOperation Packageのパターンのインスタンスの一覧を取得する
 */
export const useGetOperationPackageInstances = (ownerProduct: OwnerProduct, patternId: string, options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  targetId?: string;
}) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const targetId = options?.targetId;
  const {
    data,
    isPending,
    error
  } = useQuery({
    queryKey: operationPackageQueryKeys.instances(ownerProduct, patternId, page, pageSize, targetId),
    queryFn: () => getOperationPackageInstances(ownerProduct, patternId, {
      page,
      pageSize,
      targetId
    }),
    enabled: options?.enabled ?? true
  });
  return {
    data,
    isPending,
    error
  };
};

/** パターン配下の固まり（タグ）進捗サマリ。pageSize 上限寄りで取り切る呼び出し向け。 */
export const useGetOperationPackageInstanceTags = (ownerProduct: OwnerProduct, patternId: string, options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
}) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const {
    data,
    isPending,
    error
  } = useQuery({
    queryKey: operationPackageQueryKeys.instanceTags(ownerProduct, patternId, page, pageSize),
    queryFn: () => getOperationPackageInstanceTags(ownerProduct, patternId, {
      page,
      pageSize
    }),
    enabled: (options?.enabled ?? true) && Boolean(patternId)
  });
  return {
    data,
    isPending,
    error
  };
};

/**
 * 行政申請「確認画面」一覧（タグ別 Step 行）
 */
export const useGetEgovApplicationStepsByTag = (ownerProduct: OwnerProduct, tagId: string, options?: {
  enabled?: boolean;
} & Omit<EgovApplicationStepsByTagQuery, 'tagId'>) => {
  const enabled = options?.enabled ?? true;
  const operationPackagePatternId = options?.operationPackagePatternId ?? '';
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const orderBy = options?.orderBy;
  const orderDirection = options?.orderDirection;
  const searchText = options?.searchText;
  const status = options?.status;
  const queryKey = operationPackageQueryKeys.egovApplicationStepsByTag(ownerProduct, tagId, operationPackagePatternId, {
    page,
    pageSize,
    orderBy,
    orderDirection,
    searchText,
    status
  });
  const {
    data,
    isPending,
    error
  } = usePollingQuery({
    ...QUERY_PRESETS.list(),
    queryKey,
    queryFn: () => getEgovApplicationStepsByTag(tagId, {
      operationPackagePatternId,
      page,
      pageSize,
      orderBy,
      orderDirection: orderDirection ?? 'asc',
      searchText,
      status
    }),
    enabled: enabled && Boolean(tagId) && Boolean(operationPackagePatternId),
    polling: {
      shouldPoll: listData => hasEgovApplicationBatchProcessingRows(listData?.items ?? [])
    }
  });
  return {
    data,
    isPending,
    error
  };
};

/**
 * 年末調整 内容確認一覧（タグ別インスタンス行）
 */
export const useGetYearEndAdjustmentInstancesByTag = (ownerProduct: OwnerProduct, tagId: string, options?: {
  enabled?: boolean;
  meta?: {
    disableGlobalErrorHandling?: boolean;
  };
} & Omit<YearEndAdjustmentInstancesByTagQuery, 'tagId'>) => {
  const enabled = options?.enabled ?? true;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const orderBy = options?.orderBy;
  const orderDirection = options?.orderDirection;
  const searchText = options?.searchText;
  const status = options?.status;
  const documentFocus = options?.documentFocus;
  const {
    data,
    isPending,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: operationPackageQueryKeys.yearEndAdjustmentInstancesByTag(ownerProduct, tagId, {
      page,
      pageSize,
      orderBy,
      orderDirection,
      searchText,
      status,
      documentFocus
    }),
    queryFn: () => getYearEndAdjustmentInstancesByTag(tagId, {
      page,
      pageSize,
      orderBy,
      orderDirection: orderDirection ?? 'asc',
      searchText,
      status,
      documentFocus
    }),
    enabled: enabled && Boolean(tagId),
    meta: options?.meta
  });
  return {
    data,
    isPending,
    isError,
    error,
    refetch
  };
};

/**
 * 年末調整 内容確認 書類タブ別件数（searchText / status は一覧と同期）
 */
export const useGetYearEndAdjustmentDocumentTabCounts = (ownerProduct: OwnerProduct, tagId: string, options?: {
  enabled?: boolean;
  meta?: {
    disableGlobalErrorHandling?: boolean;
  };
} & Omit<YearEndAdjustmentDocumentTabCountsQuery, 'tagId'>) => {
  const enabled = options?.enabled ?? true;
  const searchText = options?.searchText;
  const status = options?.status;
  const {
    data,
    isPending,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: operationPackageQueryKeys.yearEndAdjustmentDocumentTabCounts(ownerProduct, tagId, {
      searchText,
      status
    }),
    queryFn: () => getYearEndAdjustmentDocumentTabCounts(tagId, {
      searchText,
      status
    }),
    enabled: enabled && Boolean(tagId),
    meta: options?.meta
  });
  return {
    data,
    isPending,
    isError,
    error,
    refetch
  };
};

/**
 * 年末調整 インスタンス書類一覧（ドロワー用・itemKey でキー付け）。
 * 書類ごとに itemKey・formTaskInstanceId・confirmationTicketId が返るため、
 * ドロワーの書類→タスク対応を itemKey で直接解決できる。
 */
export const useGetYearEndAdjustmentInstanceDocuments = (ownerProduct: OwnerProduct, instanceId: string | null, options?: {
  enabled?: boolean;
  meta?: {
    disableGlobalErrorHandling?: boolean;
  };
}) => {
  const enabled = (options?.enabled ?? true) && Boolean(instanceId);
  const {
    data,
    isPending,
    error
  } = useQuery({
    queryKey: operationPackageQueryKeys.yearEndAdjustmentInstanceDocuments(ownerProduct, instanceId ?? ''),
    queryFn: async (): Promise<YearEndAdjustmentInstanceDocumentsResponse> => {
      if (instanceId == null) {
        throw new Error('instanceId is required');
      }
      return getYearEndAdjustmentInstanceDocuments(instanceId);
    },
    enabled,
    meta: options?.meta
  });
  return {
    data,
    isPending,
    error
  };
};

/**
 * 行政申請「確認画面」一覧（アーカイブ済み Package Instance 指定）
 */
export const useGetEgovApplicationStepsByPackageInstances = (ownerProduct: OwnerProduct, operationPackageInstanceIds: readonly string[], options?: {
  enabled?: boolean;
} & EgovApplicationStepsByPackageInstancesQuery) => {
  const enabled = options?.enabled ?? true;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const orderBy = options?.orderBy;
  const orderDirection = options?.orderDirection ?? 'asc';
  const searchText = options?.searchText;
  const status = options?.status;
  const queryKey = operationPackageQueryKeys.egovApplicationStepsByPackageInstances(ownerProduct, operationPackageInstanceIds, {
    page,
    pageSize,
    orderBy,
    orderDirection,
    searchText,
    status
  });
  const {
    data,
    isPending,
    error
  } = useQuery({
    ...QUERY_PRESETS.list(),
    queryKey,
    queryFn: () => getEgovApplicationStepsByPackageInstances(operationPackageInstanceIds, {
      page,
      pageSize,
      orderBy,
      orderDirection,
      searchText,
      status
    }),
    enabled: enabled && operationPackageInstanceIds.length > 0
  });
  return {
    data,
    isPending,
    error
  };
};

/**
 * Operation Package Instance タグ（タグ ID から id / name を取得）
 */
export const useGetPackageInstanceTag = (ownerProduct: OwnerProduct, tagId: string, options?: {
  enabled?: boolean;
}) => {
  const {
    data,
    isPending,
    error
  } = useQuery({
    queryKey: operationPackageQueryKeys.packageInstanceTag(ownerProduct, tagId),
    queryFn: () => getPackageInstanceTag(ownerProduct, tagId),
    enabled: (options?.enabled ?? true) && Boolean(tagId)
  });
  return {
    data,
    isPending,
    error
  };
};